import { create } from 'zustand';
import { fetchCommunityTopics } from '@/service/api/community';

interface CommunityTopicState {
  /** 启用的社区话题列表 */
  topics: Api.Community.TopicResponse[];
  /** 是否正在加载中 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;

  /** 获取启用的社区话题列表 (支持 force 强制刷新) */
  fetchTopics: (force?: boolean) => Promise<Api.Community.TopicResponse[]>;
  /** 根据 ID 获取话题详情对象 */
  getTopicById: (id?: string) => Api.Community.TopicResponse | undefined;
  /** 根据 ID 获取话题名称 */
  getTopicNameById: (id?: string, fallback?: string) => string;
  /** 清除错误信息 */
  clearError: () => void;
}

let fetchTopicsPromise: Promise<Api.Community.TopicResponse[]> | null = null;

export const useCommunityTopicStore = create<CommunityTopicState>((set, get) => ({
  topics: [],
  loading: false,
  error: null,

  fetchTopics: async (force = false) => {
    const { topics } = get();
    if (!force && topics.length > 0) {
      return topics;
    }
    if (fetchTopicsPromise) {
      return fetchTopicsPromise;
    }

    set({ loading: true, error: null });
    fetchTopicsPromise = fetchCommunityTopics()
      .then((res) => {
        const list = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res)
          ? (res as unknown as Api.Community.TopicResponse[])
          : [];
        set({ topics: list, loading: false, error: null });
        return list;
      })
      .catch((err) => {
        const errorMessage = err instanceof Error ? err.message : '获取社区话题列表失败';
        set({ loading: false, error: errorMessage });
        throw err;
      })
      .finally(() => {
        fetchTopicsPromise = null;
      });

    return fetchTopicsPromise;
  },

  getTopicById: (id) => {
    if (!id) return undefined;
    const { topics } = get();
    return topics.find((item) => item.id === id);
  },

  getTopicNameById: (id, fallback = '') => {
    if (!id) return fallback;
    const { topics } = get();
    const target = topics.find((item) => item.id === id);
    return target?.name || fallback || id;
  },

  clearError: () => set({ error: null }),
}));
